import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ClubPicker from './ClubPicker';
import RequestSentScreen from './RequestSentScreen';
import { ClubListItem } from '../types/api';
import { useAppSelector } from '../app/hooks';
import { useJoinClubMutation } from '../api/apiSlice';

export default function FindClub() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const token = useAppSelector((state) => state.auth.token);
    const [joinClub] = useJoinClubMutation();
    const [joinedClubName, setJoinedClubName] = useState<string | null>(null);
    const [error, setError] = useState('');

    const handleSelect = async (club: ClubListItem) => {
        if (!token) {
            const params = new URLSearchParams({ clubId: String(club.ID), clubName: club.NAME });
            navigate(`/signup?${params.toString()}`);
            return;
        }

        // Already logged in: we already have this person's profile, so
        // there's no form to fill in -- just request access directly.
        setError('');
        try {
            await joinClub({ clubId: club.ID }).unwrap();
            setJoinedClubName(club.NAME);
        } catch (err: any) {
            setError(err?.data?.message ?? t('common.genericError'));
        }
    };

    if (joinedClubName !== null) {
        return <RequestSentScreen clubName={joinedClubName} ctaLabel={t('clubs.backToActivities')} ctaHref="/activities" />;
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                {t('clubs.findYourClub')}
            </Typography>
            <Typography color="text.secondary" gutterBottom>
                {t('clubs.findYourClubSubtitle')}
            </Typography>
            {error && (
                <Typography color="error" gutterBottom>
                    {error}
                </Typography>
            )}
            <ClubPicker onSelect={handleSelect} variant="cards" />
        </Container>
    );
}
